import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
  PageHeader,
} from '@nexply/ui';
import type { MemberDTO, MemberUpdateInput } from '@nexply/api-client';
import { api } from '../../lib/api.js';
import { QRModal } from '../../components/QRModal.js';
import { PhotoUpload } from '../../components/PhotoUpload.js';

const empty = {
  firstName: '',
  lastName: '',
  company: '',
  role: '',
  industry: '',
  email: '',
  phone: '',
  website: '',
  linkedin: '',
  instagram: '',
  xing: '',
  street: '',
  zip: '',
  city: '',
  birthday: '',
  country: '',
  tagsRaw: '',
  photoUrl: '',
  pitch: '',
  offering: '',
  seeking: '',
  notes: '',
};

function fromMember(m: MemberDTO | null): typeof empty {
  if (!m) return empty;
  return {
    firstName: m.firstName ?? '',
    lastName: m.lastName ?? '',
    company: m.company ?? '',
    role: m.role ?? '',
    industry: m.industry ?? '',
    email: m.email ?? '',
    phone: m.phone ?? '',
    website: m.website ?? '',
    linkedin: m.linkedin ?? '',
    instagram: m.instagram ?? '',
    xing: m.xing ?? '',
    street: m.street ?? '',
    zip: m.zip ?? '',
    city: m.city ?? '',
    birthday: m.birthday ?? '',
    country: m.country ?? '',
    tagsRaw: m.tags.join(', '),
    photoUrl: m.photoUrl ?? '',
    pitch: m.pitch ?? '',
    offering: m.offering ?? '',
    seeking: m.seeking ?? '',
    notes: m.notes ?? '',
  };
}

export function ProfilePage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => api.profile.me(),
  });

  const [form, setForm] = useState(empty);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  // Solange der Nutzer tippt, darf ein Refetch (z.B. beim Fenster-Fokus) das
  // Formular nicht mit Server-Daten überschreiben — das hat Eingaben gefressen.
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (data && !dirtyRef.current) setForm(fromMember(data.member));
  }, [data]);

  const save = useMutation({
    mutationFn: (input: MemberUpdateInput) => api.profile.save(input),
    onSuccess: () => {
      dirtyRef.current = false;
      qc.invalidateQueries({ queryKey: ['profile', 'me'] });
      setSavedAt(Date.now());
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const tags = form.tagsRaw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    save.mutate({
      firstName: form.firstName.trim() || undefined,
      lastName: form.lastName.trim() || undefined,
      company: form.company || null,
      role: form.role || null,
      industry: form.industry || null,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      linkedin: form.linkedin || null,
      instagram: form.instagram || null,
      xing: form.xing || null,
      street: form.street || null,
      zip: form.zip || null,
      city: form.city || null,
      birthday: form.birthday || null,
      country: form.country || null,
      tags,
      photoUrl: form.photoUrl || null,
      pitch: form.pitch || null,
      offering: form.offering || null,
      seeking: form.seeking || null,
      notes: form.notes || null,
    });
  }

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Über einen Timeout ausblenden — eine reine Date.now()-Rechnung würde ohne
  // Re-Render nie verschwinden (oder ewig stehen bleiben).
  const recentlySaved = savedAt !== null;
  useEffect(() => {
    if (savedAt === null) return;
    const id = setTimeout(() => setSavedAt(null), 4000);
    return () => clearTimeout(id);
  }, [savedAt]);

  const displayName = [form.firstName, form.lastName].filter(Boolean).join(' ');
  const initials = ((form.firstName[0] ?? '') + (form.lastName[0] ?? '') || '?').toUpperCase();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mein Business-Profil"
        subtitle="Diese Angaben werden für Matching, Karten- und Community-Ansichten verwendet."
      />

      {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}

      {!isLoading && (
        <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <Card>
                <CardBody className="flex flex-col items-center text-center py-7">
                  {form.photoUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={form.photoUrl} alt="" className="h-24 w-24 rounded-full object-cover border border-line" />
                  ) : (
                    <div className="grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-gold-light to-gold-dark text-[#1A1714] text-2xl font-semibold">
                      {initials}
                    </div>
                  )}
                  <div className="mt-4 text-base font-semibold text-text">{displayName || 'Dein Name'}</div>
                  {form.role && <div className="text-xs text-text-dim">{form.role}</div>}
                  {form.company && <div className="text-xs text-text-muted">{form.company}</div>}
                  {form.city && (
                    <div className="mt-2 text-[11px] uppercase tracking-wider text-text-muted">
                      {[form.city, form.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {form.website && (
                    <Button type="button" variant="ghost" size="sm" className="mt-4" onClick={() => setQrOpen(true)}>
                      QR-Code teilen
                    </Button>
                  )}
                </CardBody>
              </Card>
              <div className="text-[11px] text-text-muted leading-relaxed px-1">
                Tipp: Je vollständiger dein Profil, desto besser werden die Matching-Vorschläge.
              </div>
            </div>
          </aside>

        <form onSubmit={submit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Über mich</CardTitle>
            </CardHeader>
            <CardBody className="pt-0 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Vorname</Label>
                <Input
                  value={form.firstName}
                  onChange={(e) => upd('firstName', e.currentTarget.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input
                  value={form.lastName}
                  onChange={(e) => upd('lastName', e.currentTarget.value)}
                  autoComplete="family-name"
                />
              </div>
              <div>
                <Label>Firma</Label>
                <Input
                  value={form.company}
                  onChange={(e) => upd('company', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Position / Rolle</Label>
                <Input value={form.role} onChange={(e) => upd('role', e.currentTarget.value)} />
              </div>
              <div>
                <Label>Branche</Label>
                <Input
                  value={form.industry}
                  onChange={(e) => upd('industry', e.currentTarget.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Foto</Label>
                <PhotoUpload
                  value={form.photoUrl}
                  onChange={(v) => upd('photoUrl', v)}
                  fallbackInitials={initials}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Kontakt</CardTitle>
            </CardHeader>
            <CardBody className="pt-0 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => upd('email', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => upd('phone', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Website</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.website}
                    onChange={(e) => upd('website', e.currentTarget.value)}
                  />
                  {form.website && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setQrOpen(true)}>
                      QR
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input
                  value={form.linkedin}
                  onChange={(e) => upd('linkedin', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input
                  value={form.instagram}
                  onChange={(e) => upd('instagram', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>XING</Label>
                <Input value={form.xing} onChange={(e) => upd('xing', e.currentTarget.value)} />
              </div>
              <div>
                <Label>Straße &amp; Hausnummer</Label>
                <Input
                  value={form.street}
                  onChange={(e) => upd('street', e.currentTarget.value)}
                  autoComplete="street-address"
                />
              </div>
              <div>
                <Label>PLZ</Label>
                <Input
                  value={form.zip}
                  onChange={(e) => upd('zip', e.currentTarget.value)}
                  autoComplete="postal-code"
                />
              </div>
              <div>
                <Label>Stadt</Label>
                <Input value={form.city} onChange={(e) => upd('city', e.currentTarget.value)} />
              </div>
              <div>
                <Label>Land</Label>
                <Input
                  value={form.country}
                  onChange={(e) => upd('country', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Geburtstag</Label>
                <Input
                  type="date"
                  value={form.birthday}
                  onChange={(e) => upd('birthday', e.currentTarget.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Tags (kommagetrennt)</Label>
                <Input
                  value={form.tagsRaw}
                  onChange={(e) => upd('tagsRaw', e.currentTarget.value)}
                  placeholder="z.B. KI, B2B, Vertrieb"
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mein Angebot</CardTitle>
            </CardHeader>
            <CardBody className="pt-0 space-y-3">
              <div>
                <Label>Pitch</Label>
                <Textarea
                  value={form.pitch}
                  onChange={(e) => upd('pitch', e.currentTarget.value)}
                  placeholder="Wer du bist und was du tust – in 2-3 Sätzen."
                />
              </div>
              <div>
                <Label>Was bietest du an?</Label>
                <Textarea
                  value={form.offering}
                  onChange={(e) => upd('offering', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Was suchst du?</Label>
                <Textarea
                  value={form.seeking}
                  onChange={(e) => upd('seeking', e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>Persönliche Notizen</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => upd('notes', e.currentTarget.value)}
                />
              </div>
            </CardBody>
          </Card>

          <div className="flex items-center justify-end gap-3">
            {recentlySaved && (
              <span className="text-xs text-success">Gespeichert.</span>
            )}
            {save.isError && (
              <span className="text-xs text-danger">
                {save.error instanceof Error && save.error.message !== 'invalid_input'
                  ? save.error.message
                  : 'Speichern fehlgeschlagen — bitte Eingaben prüfen.'}
              </span>
            )}
            <Button type="submit" disabled={save.isPending || !form.firstName.trim()}>
              {save.isPending ? 'Speichert…' : 'Speichern'}
            </Button>
          </div>
        </form>
        </div>
      )}

      {qrOpen && form.website && (
        <QRModal
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          value={form.website}
          title="QR-Code · Meine Website"
          downloadName="nexply-website-qr.png"
        />
      )}
    </div>
  );
}
